import { useState, useMemo, useEffect } from 'react';
import { ArrowRight, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useQuery } from '@tanstack/react-query';
import { fetchCampaigns } from '@/lib/apis';
import { CAMPAIGN_STATE, getCampaignType, getCampaignTypeLabel } from '@/lib/types';
import type { CampaignData, OrganizationDataWithMembership } from '@/lib/types';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import googleAds from '/google-ads.svg';

interface TransferCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetOrganization: OrganizationDataWithMembership;  // The clicked row - destination
  activeOrganization: OrganizationDataWithMembership | null;  // The currently active org - default source
  availableOrganizations: OrganizationDataWithMembership[];
  onTransfer: (campaignIds: number[], targetOrganizationId: number, sourceOrganizationId: number) => void;
  isLoading?: boolean;
}

export function TransferCampaignsModal({
  isOpen,
  onClose,
  targetOrganization,
  activeOrganization,
  availableOrganizations,
  onTransfer,
  isLoading = false,
}: TransferCampaignsModalProps) {
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>([]);
  const [sourceOrganizationId, setSourceOrganizationId] = useState<string>('');
  const [targetOrganizationId, setTargetOrganizationId] = useState<string>(targetOrganization.id.toString());
  const { symbol } = useCurrencySymbol();

  // Set initial source organization when modal opens
  useEffect(() => {
    if (isOpen) {
      // Destination is always the clicked row
      setTargetOrganizationId(targetOrganization.id.toString());
      
      // If active org is different from clicked row, use active as source
      // If they're the same, don't set a default source
      if (activeOrganization && activeOrganization.id !== targetOrganization.id) {
        setSourceOrganizationId(activeOrganization.id.toString());
      } else {
        setSourceOrganizationId('');  // Let user choose
      }
    }
  }, [isOpen, activeOrganization, targetOrganization.id]);

  // Fetch campaigns for the selected source organization
  const { data: campaignsData = [], isLoading: isLoadingCampaigns } = useQuery({
    queryKey: ['transfer-campaigns', sourceOrganizationId],
    queryFn: () => fetchCampaigns(parseInt(sourceOrganizationId)),
    enabled: !!sourceOrganizationId && isOpen && sourceOrganizationId !== targetOrganizationId,
  });

  // All campaigns from source org are transferable
  const transferableCampaigns = useMemo(() => {
    return campaignsData || [];
  }, [campaignsData]);

  // Reset selected campaigns when source org changes
  useEffect(() => {
    setSelectedCampaignIds([]);
  }, [sourceOrganizationId]);

  // Get campaign state display
  const getStateDisplay = (campaign: CampaignData) => {
    switch (campaign.state) {
      case CAMPAIGN_STATE.LIVE:
        return { text: 'Live', color: 'bg-green-100 text-green-800' };
      case CAMPAIGN_STATE.PAUSED:
        return { text: campaign.first_published_at ? 'Paused' : 'Ready', color: 'bg-amber-100 text-amber-800' };
      case CAMPAIGN_STATE.DRAFT:
        return { text: 'Ready', color: 'bg-amber-100 text-amber-800' };
      default:
        return { text: campaign.state, color: 'bg-gray-100 text-gray-900' };
    }
  };

  const handleSelectAll = () => {
    if (selectedCampaignIds.length === transferableCampaigns.length) {
      setSelectedCampaignIds([]);
    } else {
      setSelectedCampaignIds(transferableCampaigns.map(c => c.id));
    }
  };

  const handleCampaignToggle = (campaignId: number) => {
    setSelectedCampaignIds(prev => 
      prev.includes(campaignId)
        ? prev.filter(id => id !== campaignId)
        : [...prev, campaignId]
    );
  };

  const handleTransfer = () => {
    if (selectedCampaignIds.length === 0 || !targetOrganizationId || !sourceOrganizationId) {
      return;
    }
    
    onTransfer(
      selectedCampaignIds, 
      parseInt(targetOrganizationId), 
      parseInt(sourceOrganizationId)
    );
  };

  const handleClose = () => {
    setSelectedCampaignIds([]);
    setSourceOrganizationId('');
    setTargetOrganizationId(targetOrganization.id.toString());
    onClose();
  };

  const isAllSelected = selectedCampaignIds.length === transferableCampaigns.length && transferableCampaigns.length > 0;
  const isIndeterminate = selectedCampaignIds.length > 0 && selectedCampaignIds.length < transferableCampaigns.length;
  const canTransfer = selectedCampaignIds.length > 0 && targetOrganizationId && sourceOrganizationId && targetOrganizationId !== sourceOrganizationId;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] min-h-[680px] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-gray-900">Transfer Campaigns</DialogTitle>
          <DialogDescription>
            Move campaigns between organizations where you are a member.
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 flex flex-col space-y-6 overflow-hidden">
          {/* Organization Selection Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Source Organization Selection */}
            <Card className="rounded-lg border bg-white">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <label htmlFor="source-org" className="text-sm font-medium text-gray-900">
                    Transfer From
                  </label>
                  <Select value={sourceOrganizationId} onValueChange={setSourceOrganizationId}>
                    <SelectTrigger id="source-org" className="cursor-pointer">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={activeOrganization && activeOrganization.id === targetOrganization.id ? "Select source organization..." : "Select source organization..."} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOrganizations.map((org) => (
                        <SelectItem key={org.id} value={org.id.toString()}>
                          {org.name} ({org.campaign_count || 0} campaigns)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {sourceOrganizationId && (
                    <p className={`text-sm ${sourceOrganizationId === targetOrganizationId ? 'text-amber-600' : 'text-gray-700'}`}>
                      {sourceOrganizationId === targetOrganizationId 
                        ? 'Cannot transfer to the same organization' 
                        : isLoadingCampaigns 
                        ? 'Loading campaigns...' 
                        : `${transferableCampaigns.length} campaigns available`}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Target Organization Selection */}
            <Card className="rounded-lg border bg-white">
              <CardContent className="pt-6">
                <div className="space-y-3">
                  <label htmlFor="target-org" className="text-sm font-medium text-gray-900">
                    Transfer To
                  </label>
                  <Select value={targetOrganizationId} onValueChange={setTargetOrganizationId}>
                    <SelectTrigger id="target-org" className="cursor-pointer">
                      <Building2 className="h-4 w-4 mr-2" />
                      <SelectValue placeholder="Select target organization..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableOrganizations.map((org) => (
                        <SelectItem key={org.id} value={org.id.toString()}>
                          {org.name} ({org.campaign_count || 0} campaigns)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-gray-700">
                    Destination for transferred campaigns
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Campaign Selection */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Select Campaigns to Transfer</h3>
            
            {sourceOrganizationId === targetOrganizationId ? (
              <div className="text-center py-8 text-gray-500">
                Source and destination organizations cannot be the same
              </div>
            ) : isLoadingCampaigns ? (
              <div className="text-center py-8 text-gray-500">
                Loading campaigns...
              </div>
            ) : transferableCampaigns.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {sourceOrganizationId ? 'No campaigns available for transfer' : 'Select a source organization'}
              </div>
            ) : (
              <div className="rounded-lg border bg-white shadow-sm overflow-hidden">
                <div className="max-h-[320px] overflow-y-auto">
                  <Table>
                    <TableHeader className="sticky top-0 bg-white z-10">
                      <TableRow>
                        <TableHead className="py-3 px-4 text-center bg-white">
                          <Checkbox
                            checked={isAllSelected}
                            ref={(ref) => {
                              if (ref) (ref as HTMLInputElement).indeterminate = isIndeterminate;
                            }}
                            onCheckedChange={handleSelectAll}
                          />
                        </TableHead>
                        <TableHead className="py-3 px-4 text-gray-900 bg-white">Campaign</TableHead>
                        <TableHead className="py-3 px-4 text-gray-900 bg-white">Website</TableHead>
                        <TableHead className="py-3 px-4 text-gray-900 bg-white">Type</TableHead>
                        <TableHead className="py-3 px-4 text-gray-900 bg-white">State</TableHead>
                        <TableHead className="py-3 px-4 text-gray-900 bg-white">Budget</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody >
                      {transferableCampaigns.map((campaign) => {
                        const isSelected = selectedCampaignIds.includes(campaign.id);
                        const stateDisplay = getStateDisplay(campaign);
                        const campaignType = getCampaignType(campaign);
                        const displayName = campaign.name;
                        
                        return (
                          <TableRow 
                            key={campaign.id} 
                            className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-[#290BD9]/10' : ''}`}
                            onClick={() => handleCampaignToggle(campaign.id)}
                          >
                            <TableCell className="py-3 px-4 text-center">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => handleCampaignToggle(campaign.id)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell className="py-3 px-4">
                              <div className="flex items-center space-x-3">
                                <img src={googleAds} alt="Google Ads" className="w-6 h-6 flex-shrink-0" />
                                <span className="font-medium text-gray-900">{displayName}</span>
                              </div>
                            </TableCell>
                            <TableCell className="py-3 px-4 text-gray-900">
                              {campaign.website_url?.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')}
                            </TableCell>
                            <TableCell className="py-3 px-4 text-gray-900">
                              {getCampaignTypeLabel(campaignType)}
                            </TableCell>
                            <TableCell className="py-3 px-4">
                              <Badge variant="outline" className={stateDisplay.color}>
                                {stateDisplay.text}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-3 px-4 text-gray-900">
                              {campaign.budget ? `${symbol}${campaign.budget}/d` : 'Not set'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>

          {/* Transfer Summary */}
          {selectedCampaignIds.length > 0 && targetOrganizationId && sourceOrganizationId && (
            <Card className="rounded-lg border bg-blue-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Transfer Summary</p>
                    <p className="text-sm text-gray-700">
                      Moving {selectedCampaignIds.length} campaign{selectedCampaignIds.length !== 1 ? 's' : ''} from{' '}
                      <span className="font-medium">
                        {availableOrganizations.find(org => org.id.toString() === sourceOrganizationId)?.name}
                      </span> to{' '}
                      <span className="font-medium">
                        {availableOrganizations.find(org => org.id.toString() === targetOrganizationId)?.name}
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleTransfer}
            disabled={!canTransfer || isLoading}
            className="bg-[#290BD9] hover:bg-[#2309C5] text-white flex items-center space-x-2"
          >
            <span>{isLoading ? 'Transferring...' : 'Transfer Campaigns'}</span>
            {!isLoading && <ArrowRight className="h-4 w-4" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}